import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Layout } from '../components/Layout';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { useCheckOut } from '../hooks/useEvents';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowRight, CheckCircle, LogOut, User, Users } from 'lucide-react';

interface CheckInResultData {
  eventName: string;
  eventDate: string;
  eventId: string;
  attendees: Array<{
    attendeeId: string;
    attendeeName: string;
    checkedInAt: string;
    registrationStatus: string;
  }>;
  checkedInByName: string;
  totalCheckedIn: number;
}

interface SavedEvent {
  id: string;
  eventId: string;
  eventName: string;
  eventDate: string;
  attendees: Array<{
    attendeeId: string;
    attendeeName: string;
    checkedInAt: string;
    registrationStatus: string;
  }>;
  checkedInByName: string;
  totalCheckedIn: number;
  timestamp: string;
}

export const CheckInResultPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const resultData = location.state as CheckInResultData;
  const [checkedOutAttendees, setCheckedOutAttendees] = useState<Set<string>>(new Set());
  const { t } = useTranslation('common');

  const checkOutMutation = useCheckOut();

  React.useEffect(() => {
    if (resultData) {
      const savedEvents: SavedEvent[] = JSON.parse(localStorage.getItem('myEvents') || '[]');

      const eventAlreadyExists = savedEvents.some(
        (event: SavedEvent) =>
          event.eventId === resultData.eventId &&
          event.checkedInByName === resultData.checkedInByName &&
          event.totalCheckedIn === resultData.totalCheckedIn,
      );

      if (!eventAlreadyExists) {
        const eventRecord: SavedEvent = {
          id: `${resultData.eventId}-${Date.now()}`,
          eventId: resultData.eventId,
          eventName: resultData.eventName,
          eventDate: resultData.eventDate,
          attendees: resultData.attendees,
          checkedInByName: resultData.checkedInByName,
          totalCheckedIn: resultData.totalCheckedIn,
          timestamp: new Date().toISOString(),
        };

        const updatedEvents = [eventRecord, ...savedEvents].slice(0, 10);
        localStorage.setItem('myEvents', JSON.stringify(updatedEvents));
      }
    }
  }, [resultData]);

  if (!resultData) {
    navigate('/events');
    return null;
  }

  const handleCheckout = async (attendeeId: string) => {
    try {
      await checkOutMutation.mutateAsync({
        eventId: resultData.eventId,
        attendeeId,
      });

      setCheckedOutAttendees((prev) => new Set([...prev, attendeeId]));
    } catch (error) {
      const attendeeName = resultData.attendees.find((a) => a.attendeeId === attendeeId)?.attendeeName;
      toast.error(`Failed to check out ${attendeeName}. Please try again.`);
    }
  };

  const handleCheckoutAll = async () => {
    try {
      const remainingAttendees = resultData.attendees.filter((a) => !checkedOutAttendees.has(a.attendeeId));

      for (const attendee of remainingAttendees) {
        await checkOutMutation.mutateAsync({
          eventId: resultData.eventId,
          attendeeId: attendee.attendeeId,
        });
        setCheckedOutAttendees((prev) => new Set([...prev, attendee.attendeeId]));
      }

      toast.success('All attendees checked out successfully');
      setTimeout(() => navigate('/my-families'), 1500);
    } catch (error) {
      toast.error('Some attendees could not be checked out. Please try individual checkout.');
    }
  };

  const handleBackToEvents = () => {
    navigate('/events', {
      state: {
        selectedMembers: resultData.attendees.map((a) => a.attendeeId),
      },
    });
  };

  const remainingAttendeesCount = resultData.attendees.length - checkedOutAttendees.size;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="text-center py-8 px-4 bg-gradient-to-br from-[var(--color-success-50)] to-[var(--color-success-100)] rounded-2xl border border-[var(--color-success-500)]/30">
          <div className="flex justify-center mb-4">
            <div className="relative">
              <CheckCircle className="absolute -bottom-1 -right-1 h-6 w-6 bg-[var(--color-brand-white)] text-[var(--color-success-700)] rounded-full border-2 border-[var(--color-brand-white)]" />
            </div>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-[var(--color-success-700)] mb-2">
            {t('events.checkInResult.title')}
          </h1>
          <p className="text-[var(--color-success-700)] text-lg">
            {t('events.checkInResult.success', { count: resultData.totalCheckedIn })}
          </p>
        </div>

        <Card className="bg-[var(--color-brand-white)] shadow-sm">
          <CardHeader className="pb-4">
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="text-xl text-[var(--color-neutral-900)] mb-1">{resultData.eventName}</CardTitle>
                <div className="flex items-center gap-4 text-sm text-[var(--color-neutral-700)]">
                  <span className="flex items-center gap-1">
                    <User className="h-4 w-4" />
                    {t('events.checkInResult.by')} {resultData.checkedInByName}
                  </span>
                </div>
              </div>
              <Badge variant="secondary" className="flex items-center gap-1 text-sm">
                <Users className="h-4 w-4" />
                {resultData.totalCheckedIn} {t('events.attendees.total')}
              </Badge>
            </div>
          </CardHeader>
        </Card>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-[var(--color-neutral-900)]">
              {t('events.checkInResult.attendees')} ({resultData.totalCheckedIn})
            </h2>
            {remainingAttendeesCount > 1 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleCheckoutAll}
                disabled={checkOutMutation.isPending || remainingAttendeesCount === 0}
                className="text-[var(--color-error-700)] border-[var(--color-error-100)] hover:bg-[var(--color-error-100)]"
              >
                <LogOut className="h-4 w-4 mr-1" />
                {t('events.checkInResult.checkOutAll')} ({remainingAttendeesCount})
              </Button>
            )}
          </div>

          <div className="grid gap-3">
            {resultData.attendees.map((attendee) => {
              const isCheckedOut = checkedOutAttendees.has(attendee.attendeeId);
              return (
                <div
                  key={attendee.attendeeId}
                  className={`flex items-center justify-between p-4 rounded-lg border transition-all ${
                    isCheckedOut
                      ? 'bg-[var(--color-neutral-300)]/20 border-[var(--color-neutral-300)] opacity-75'
                      : 'bg-[var(--color-brand-white)] border-[var(--color-neutral-300)] hover:border-[var(--color-neutral-400)] hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-3 h-3 rounded-full ${
                        isCheckedOut ? 'bg-[var(--color-neutral-400)]' : 'bg-[var(--color-success-500)]'
                      }`}
                    />
                    <div>
                      <p className="font-medium text-[var(--color-neutral-900)]">{attendee.attendeeName}</p>
                      <p className="text-sm text-[var(--color-neutral-400)]">
                        {isCheckedOut
                          ? t('events.checkInResult.checkedOut')
                          : t('events.checkInResult.checkedInAt', {
                              time: format(new Date(attendee.checkedInAt), 'h:mm a'),
                            })}
                      </p>
                    </div>
                  </div>

                  <Button
                    variant={isCheckedOut ? 'ghost' : 'outline'}
                    size="sm"
                    onClick={() => handleCheckout(attendee.attendeeId)}
                    disabled={isCheckedOut || checkOutMutation.isPending}
                    className={
                      isCheckedOut
                        ? 'opacity-50 cursor-not-allowed'
                        : 'text-[var(--color-error-700)] border-[var(--color-error-100)] hover:bg-[var(--color-error-100)]'
                    }
                  >
                    {checkOutMutation.isPending ? (
                      t('events.checkInResult.checkingOut')
                    ) : isCheckedOut ? (
                      t('events.checkInResult.checkedOut')
                    ) : (
                      <>
                        <LogOut className="h-4 w-4 mr-1" />
                        {t('events.checkInResult.checkOut')}
                      </>
                    )}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>

        <div className="grid gap-3 mt-8">
          <Button
            size="lg"
            onClick={() => navigate('/my-events')}
            className="bg-[var(--color-success-700)] hover:bg-[var(--color-success-700)]/90 text-[var(--color-brand-white)]"
          >
            {t('events.checkInResult.viewEvents')}
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>

          <Button variant="outline" size="lg" onClick={handleBackToEvents}>
            {t('events.backToEvents')}
          </Button>
        </div>
      </div>
    </Layout>
  );
};
